import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import MayaLogo from "../components/MayaLogo";
import useUser, { setUser as persistUser } from "../hooks/useUser";
import { useAuth } from "../hooks/useAuth";
import {
  User as UserIcon,
  BadgeCheck,
  BookOpen,
  ClipboardList,
  Sparkles,
  CalendarDays,
  Receipt,
  Mail,
  Lock,
  LifeBuoy,
  Copy,
  ExternalLink,
  ChevronRight,
  Check,
  LogOut,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
};

/* -------------------- Reusable UI -------------------- */
const SectionCard = ({ title, icon: Icon, description, action, children, testid }) => (
  <section
    data-testid={testid}
    className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-9 h-9 rounded-xl bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center shrink-0">
            <Icon className="w-[18px] h-[18px]" />
          </div>
        )}
        <div>
          <h2 className="text-[16px] font-semibold text-slate-900 leading-tight">{title}</h2>
          {description && <p className="text-[13px] text-slate-500 mt-0.5">{description}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

const Field = ({ label, value, editable, onChange, type = "text", "data-testid": testid, disabled }) => (
  <label className="block">
    <span className="block text-[12px] font-medium text-slate-500 uppercase tracking-wide">{label}</span>
    {editable ? (
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/20 focus:border-[#1d4ed8] transition disabled:bg-slate-50 disabled:text-slate-400"
      />
    ) : (
      <p className="mt-1.5 text-[14px] font-medium text-slate-900">{value || "—"}</p>
    )}
  </label>
);

const PillStatus = ({ tone = "green", children }) => {
  const tones = {
    green: "bg-[#e6f7ee] text-[#15803d] border-[#c1e9d0]",
    blue: "bg-[#eaf1ff] text-[#1d4ed8] border-[#c9d9ff]",
    amber: "bg-[#fef3c7] text-[#b45309] border-[#fcd88a]",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-full border ${tones[tone]}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> {children}
    </span>
  );
};

/* -------------------- Feature access rows -------------------- */
const FeatureRow = ({ icon: Icon, iconNode, title, subtitle, right }) => (
  <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 rounded-xl bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center shrink-0">
        {iconNode || (Icon && <Icon className="w-5 h-5" />)}
      </div>
      <div className="min-w-0">
        <p className="text-[14px] font-medium text-slate-900 truncate">{title}</p>
        {subtitle && <p className="text-[12px] text-slate-500 truncate">{subtitle}</p>}
      </div>
    </div>
    <div className="shrink-0">{right}</div>
  </div>
);

/* -------------------- Maya allowance meter -------------------- */
const AllowanceMeter = ({ used, limit }) => {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="text-[28px] font-semibold text-slate-900 leading-none">
          {used}<span className="text-slate-400 text-[20px] font-medium">/{limit}</span>
        </p>
        <span className="text-[12px] font-medium text-slate-500">stations used</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: pct >= 90 ? "#dc2626" : pct >= 60 ? "#f59e0b" : "#1d4ed8",
          }}
        />
      </div>
    </div>
  );
};

/* -------------------- Page -------------------- */
const Profile = () => {
  const user = useUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [loggingOut, setLoggingOut] = useState(false);

  const [usage, setUsage] = useState({ used: 0, limit: 15, remaining: 15, resets_at: null });

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      toast.success("You have been signed out.");
      navigate("/login", { replace: true });
    } catch (_) {
      // logout() already tolerates failures locally
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => {
    setForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
    });
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const [u, b] = await Promise.all([
          axios.get(`${API}/me/station-usage`).catch(() => ({ data: null })),
          axios.get(`${API}/me/billing`).catch(() => ({ data: null })),
        ]);
        if (u.data) setUsage(u.data);
        if (b.data) setBilling(b.data);
      } catch (_) { /* noop */ }
    })();
  }, []);

  const [billing, setBilling] = useState(null);

  /* --- Real Stripe-derived subscription info --- */
  const subscription = useMemo(() => {
    const planKey = billing?.plan_key || "";
    const plan = planKey === "bundle"
      ? "Complete Preparation Bundle"
      : (billing?.plan_name || (planKey === "resources" ? "Medily Resources" : "Medily Resources"));
    const unit = billing?.unit_amount;
    const currency = (billing?.currency || "gbp").toUpperCase();
    const interval = billing?.interval || "month";
    const priceLabel = typeof unit === "number"
      ? `£${(unit / 100).toFixed(2)} / ${interval}`
      : "";
    return {
      plan,
      priceLabel,
      startedAt: billing?.period_start || null,
      renewsAt: billing?.period_end || null,
      billingInterval: interval === "month" ? "Monthly" : interval,
    };
  }, [billing]);

  const savePersonal = async () => {
    // Personal information is read-only in Stage 4.
  };

  const submitEmailChange = () => {
    if (!/^\S+@\S+\.\S+$/.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    persistUser({ email: newEmail });
    setEmailModal(false);
    setNewEmail("");
    toast.success("Email updated. Confirmation sent to your inbox.");
  };

  const submitPasswordChange = () => {
    if (!pw.current || !pw.next || pw.next.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    // Auth not wired yet — placeholder
    toast.success("Password updated successfully");
    setPasswordModal(false);
    setPw({ current: "", next: "", confirm: "" });
  };

  const copySupport = () => {
    navigator.clipboard?.writeText("info@medily.co");
    toast.success("info@medily.co copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />

        <main className="p-6 lg:p-10 max-w-[1180px] mx-auto space-y-6">
          {/* Page header */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[28px] font-semibold text-slate-900 leading-tight">Profile & Account</p>
              <p className="text-slate-500 text-[14px] mt-0.5">Manage your personal details, subscription and account settings.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center text-[15px] font-semibold border border-[#dbe6ff]">
                {(user.firstName || "?").slice(0, 1).toUpperCase()}
              </div>
              <div>
                <p className="text-[14px] font-medium text-slate-900">{user.firstName} {user.lastName}</p>
                <p className="text-[12px] text-slate-500">{user.email}</p>
              </div>
            </div>
          </div>

          {/* 1. Personal Information */}
          <SectionCard
            testid="profile-personal-info"
            icon={UserIcon}
            title="Personal Information"
          >
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="First name" value={form.firstName} data-testid="personal-firstname" />
              <Field label="Last name" value={form.lastName} data-testid="personal-lastname" />
              <Field label="Email address" value={form.email} type="email" data-testid="personal-email" />
            </div>
          </SectionCard>

          {/* 2. Subscription & Membership */}
          <SectionCard
            testid="profile-subscription"
            icon={BadgeCheck}
            title="Subscription & Membership"
            description="Your current plan and renewal information."
          >
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wide">Current plan</p>
                <p className="mt-1.5 text-[18px] font-semibold text-slate-900" data-testid="subscription-plan-name">{subscription.plan}</p>
                <p className="text-[12px] text-slate-500">{subscription.priceLabel}{subscription.priceLabel && subscription.billingInterval ? " · " : ""}{subscription.billingInterval}</p>
              </div>
              <div>
                <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wide">Started</p>
                <p className="mt-1.5 text-[14px] font-medium text-slate-900" data-testid="subscription-started-at">{fmtDate(subscription.startedAt)}</p>
              </div>
              <div>
                <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wide">Renews on</p>
                <p className="mt-1.5 text-[14px] font-medium text-slate-900" data-testid="subscription-renews-at">{fmtDate(subscription.renewsAt)}</p>
              </div>
              <div className="md:justify-self-end flex md:flex-col gap-2">
                <a
                  href="https://billing.stripe.com/p/login/14A7sL5Sj6Ct0B34hQ9IQ00"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="manage-subscription-btn"
                  className="text-[13px] font-medium px-4 py-2 rounded-full bg-[#1d4ed8] text-white hover:bg-[#1e40af] transition inline-flex items-center gap-1.5"
                >
                  Manage subscription <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </SectionCard>

          {/* 3. Medily Membership (feature access) */}
          <SectionCard
            testid="profile-membership"
            icon={Sparkles}
            title="Medily Membership"
            description="Features included with your current plan."
          >
            <div className="divide-y divide-slate-100">
              <FeatureRow
                icon={BookOpen}
                title="Study Resources"
                subtitle="Full access to all guides and topics"
                right={<PillStatus tone="green">Included</PillStatus>}
              />
              <FeatureRow
                icon={ClipboardList}
                title="Interview Tips"
                subtitle="Every scenario and worked example"
                right={<PillStatus tone="green">Included</PillStatus>}
              />
              <FeatureRow
                iconNode={<MayaLogo variant="on-white" className="h-5" />}
                title="Online Mock Interviews (Maya stations)"
                subtitle={`${usage.remaining ?? 0} stations remaining this period`}
                right={<PillStatus tone="blue">{usage.remaining ?? 0} left</PillStatus>}
              />
            </div>
          </SectionCard>

          {/* 4. Maya Usage */}
          <SectionCard
            testid="profile-maya-usage"
            icon={CalendarDays}
            title="Maya Usage"
            description="Your monthly station allowance and reset schedule."
          >
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <AllowanceMeter used={usage.used || 0} limit={usage.limit || 15} />
                <p className="text-[13px] text-slate-500 mt-3">
                  {usage.remaining > 0
                    ? <>You have <span className="font-semibold text-slate-900">{usage.remaining}</span> station{usage.remaining === 1 ? "" : "s"} remaining until the reset date.</>
                    : <>You've used your full allowance this period. Your allowance will refresh on the reset date below.</>}
                </p>
              </div>
              <div className="rounded-xl bg-[#f5f8fd] border border-slate-100 p-4">
                <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wide">Resets on</p>
                <p className="mt-1 text-[18px] font-semibold text-slate-900" data-testid="usage-resets-at">{fmtDate(usage.resets_at)}</p>
                <p className="text-[12px] text-slate-500 mt-1">Tied to your Stripe billing cycle.</p>
              </div>
            </div>
          </SectionCard>

          {/* Help & Support — Billing and Account Settings cards removed per Stage 4 */}

          {/* 7. Help & Support */}

          {/* 7. Help & Support */}
          <SectionCard
            testid="profile-support"
            icon={LifeBuoy}
            title="Help & Support"
            description="Our team typically responds within one business day."
          >
            <div className="flex items-center justify-between gap-4 flex-wrap p-4 rounded-xl bg-gradient-to-r from-[#eef4ff] to-white border border-[#dbe6ff]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-[#1d4ed8] border border-[#dbe6ff] flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-slate-900">Contact support</p>
                  <a href="mailto:info@medily.co" className="text-[13px] text-[#1d4ed8] hover:underline">info@medily.co</a>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copySupport}
                  data-testid="copy-support-email"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-[#1d4ed8] hover:text-[#1d4ed8] transition"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <a
                  href="mailto:info@medily.co"
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-full bg-[#1d4ed8] text-white hover:bg-[#1e40af] transition"
                >
                  Email us <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </SectionCard>

          <p className="text-center text-[12px] text-slate-400 pt-2 pb-6">
            Your profile data is private and visible only to you.
          </p>
        </main>
      </div>

      {/* --- Change email modal --- */}
      {emailModal && (
        <Modal onClose={() => setEmailModal(false)} title="Change email address" testid="email-modal">
          <p className="text-[13px] text-slate-500">We'll send a confirmation link to your new email address before switching.</p>
          <Field label="New email address" editable type="email" value={newEmail} onChange={setNewEmail} data-testid="new-email-input" />
          <ModalActions>
            <button onClick={() => setEmailModal(false)} className="text-[13px] font-medium px-4 py-2 rounded-full border border-slate-200 text-slate-700 hover:border-slate-300 transition">Cancel</button>
            <button onClick={submitEmailChange} data-testid="submit-email-btn" className="text-[13px] font-medium px-4 py-2 rounded-full bg-[#1d4ed8] text-white hover:bg-[#1e40af] transition inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Send confirmation
            </button>
          </ModalActions>
        </Modal>
      )}

      {/* --- Change password modal --- */}
      {passwordModal && (
        <Modal onClose={() => setPasswordModal(false)} title="Change password" testid="password-modal">
          <div className="space-y-3">
            <Field label="Current password" editable type="password" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} data-testid="current-password" />
            <Field label="New password" editable type="password" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} data-testid="new-password" />
            <Field label="Confirm new password" editable type="password" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} data-testid="confirm-password" />
            <p className="text-[12px] text-slate-500">Use at least 8 characters with a mix of letters and numbers.</p>
          </div>
          <ModalActions>
            <button onClick={() => setPasswordModal(false)} className="text-[13px] font-medium px-4 py-2 rounded-full border border-slate-200 text-slate-700 hover:border-slate-300 transition">Cancel</button>
            <button onClick={submitPasswordChange} data-testid="submit-password-btn" className="text-[13px] font-medium px-4 py-2 rounded-full bg-[#1d4ed8] text-white hover:bg-[#1e40af] transition inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Update password
            </button>
          </ModalActions>
        </Modal>
      )}
    </div>
  );
};

/* -------------------- Modal helpers -------------------- */
const Modal = ({ title, onClose, children, testid }) => (
  <div
    className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4"
    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    data-testid={testid}
  >
    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 animate-in fade-in zoom-in-95 duration-200">
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-[16px] font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
      </div>
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  </div>
);
const ModalActions = ({ children }) => (
  <div className="mt-5 flex items-center justify-end gap-2">{children}</div>
);

export default Profile;
